import store from '@/store'

/**
 * 全域 Loading 反饋工具
 * 使用方式類似 $alert，支援三態反饋
 *
 * 使用範例：
 *
 * 1. 完整三態反饋（送出訂單等）
 *    this.$loading.show({ message: '處理中...', successText: '成功', errorText: '失敗' })
 *    this.$loading.success()  // 或 this.$loading.error()
 *
 * 2. 僅 loading 模式（載入菜單等，載完就關）
 *    this.$loading.loading('載入中...')
 *    this.$loading.done()
 *
 * 3. 錯誤時顯示重整按鈕（特殊處理）
 *    this.$loading.show({
 *        message: '處理中...',
 *        errorText: '發生錯誤',
 *        showRefreshOnError: true
 *    })
 *    this.$loading.error('錯誤訊息')  // 顯示錯誤並帶有重整按鈕
 *
 * 4. 多階段任務（App 初始化等）
 *    await this.$loading.runStages(stages)
 *    // 失敗時自動顯示錯誤訊息和重整按鈕（預設行為）
 *    // 若不需要重整按鈕：
 *    await this.$loading.runStages(stages, { showRefreshOnError: false })
 */
export var globalLoading = {
	/**
	 * 顯示 Loading（完整三態模式）
	 * @param {Object|string} options - 配置物件或 loading 訊息
	 */
	show: function (options) {
		const payload =
			typeof options === 'string' ? { message: options } : options || {}
		store.commit('SHOW_GLOBAL_LOADING', payload)
	},

	/**
	 * 設置成功狀態
	 * @param {string} text - 成功訊息（可選）
	 */
	success: function (text) {
		store.commit('SET_GLOBAL_LOADING_RESULT', {
			status: 'success',
			successText: text,
		})
	},

	/**
	 * 設置失敗狀態
	 * @param {string} text - 失敗訊息（可選）
	 */
	error: function (text) {
		store.commit('SET_GLOBAL_LOADING_RESULT', {
			status: 'error',
			errorText: text,
		})
	},

	/**
	 * 僅 Loading 模式（不顯示結果反饋）
	 * @param {string} message - loading 訊息
	 */
	loading: function (message) {
		store.commit('SHOW_GLOBAL_LOADING', {
			message: message || '載入中...',
			showResult: false,
		})
	},

	/**
	 * 僅 Loading 模式結束
	 */
	done: function () {
		store.commit('HIDE_GLOBAL_LOADING')
	},

	/**
	 * 強制隱藏（不顯示結果）
	 */
	hide: function () {
		store.commit('HIDE_GLOBAL_LOADING')
	},

	/**
	 * 更新 Loading 訊息（用於多階段顯示）
	 * @param {string} message - 新的訊息文字
	 */
	updateMessage: function (message) {
		store.commit('UPDATE_GLOBAL_LOADING_MESSAGE', message)
	},

	/**
	 * 執行多階段任務（用於取代 StageLoader）
	 * @param {Array} stages - 階段陣列 [{ text: string, executor: Function, minDuration?: number }]
	 * @param {Object} options - 選項 { onComplete?: Function, onError?: Function, showRefreshOnError?: boolean }
	 * @returns {Promise<boolean>} - 是否全部成功
	 */
	runStages: async function (stages, options = {}) {
		const { onComplete, onError, showRefreshOnError = true } = options

		if (!stages || stages.length === 0) {
			if (onComplete) onComplete()
			return true
		}

		this.show({
			message: stages[0]?.text || '載入中...',
			showResult: true,
			showRefreshOnError: showRefreshOnError,
			errorText: '初始化失敗',
		})

		try {
			for (let i = 0; i < stages.length; i++) {
				// 檢查 loading 是否已被關閉（例如頁面跳轉），若已關閉則中止執行
				if (!store.state.globalLoading.visible) {
					return false
				}

				const stage = stages[i]
				const startTime = Date.now()

				this.updateMessage(stage.text)

				const result = await Promise.resolve(stage.executor())

				// 執行後再次檢查，因為 executor 可能觸發了頁面跳轉
				if (!store.state.globalLoading.visible) {
					return false
				}

				if (result !== true) {
					throw new Error(stage.text + ' 執行失敗')
				}

				const elapsed = Date.now() - startTime
				const minDuration = stage.minDuration || 0
				if (elapsed < minDuration) {
					await new Promise(resolve =>
						setTimeout(resolve, minDuration - elapsed),
					)
				}
			}

			this.hide()
			if (onComplete) onComplete()
			return true
		} catch (error) {
			// 若 loading 已被關閉，不顯示錯誤
			if (!store.state.globalLoading.visible) {
				return false
			}
			console.error('[globalLoading.runStages] 執行失敗:', error)
			const errorMessage =
				(typeof error === 'string' ? error : error?.message) ||
				'初始化失敗，請重新整理頁面'
			this.error(errorMessage)
			if (onError) onError(error)
			return false
		}
	},
}

export default function install(Vue) {
	Vue.prototype.$loading = globalLoading
}
